import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Alert,
  Button,
  InputNumber,
  Layout,
  Radio,
  Space,
  Spin,
  Switch,
  Tooltip
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  fetchDefaultScoringConfig,
  saveDefaultScoringConfig
} from '../../../redux/actions/trm/defaultScoringConfig';
import { fetchAMLSAlertLevel } from '../../../redux/actions/common';
import AlertLevelBandSlider from '../../trm/configureInstanceDashboard/AlertScoringTab/AlertLevelBandSlider';
import {
  DEFAULT_DEVIATION_MAX_CONFIG,
  scoringConfigEquals
} from '../../trm/configureInstanceDashboard/AlertScoringTab/defaults';
import {
  AGGREGATION_LABELS,
  FIELD_LABELS,
  TIPS,
  bandErrors,
  deviationErrors
} from '../../trm/configureInstanceDashboard/AlertScoringTab/scoringCopy';
import style from '../../trm/configureInstanceDashboard/AlertScoringTab/index.scss';

const { Content } = Layout;

const InfoTip = ({ title }) => (
  <Tooltip title={title} placement="topLeft">
    <InfoCircleOutlined className={style['info-icon']} />
  </Tooltip>
);
InfoTip.propTypes = { title: PropTypes.node.isRequired };

const FieldLabel = ({ children, tip, htmlFor }) => (
  <label className={style['field-label']} htmlFor={htmlFor}>
    {children}
    {tip && <InfoTip title={tip} />}
  </label>
);
FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  tip: PropTypes.node,
  htmlFor: PropTypes.string
};
FieldLabel.defaultProps = { tip: null, htmlFor: undefined };

const PanelTitle = ({ children, tip }) => (
  <div className={style['panel-title']}>
    {children}
    {tip && <InfoTip title={tip} />}
  </div>
);
PanelTitle.propTypes = {
  children: PropTypes.node.isRequired,
  tip: PropTypes.node
};
PanelTitle.defaultProps = { tip: null };

// FIN-12215: Settings > Scoring — tenant-level default scoring configuration.
// Same look/feel as the per-scenario Alert Scoring tab, but talks to the
// global-defaults endpoint (GET/PUT /tm/iad/scoringConfig/defaults, scope
// FRAUD_PREVENTION). No delete; Reset reverts the form to the shipped defaults
// in-UI and is enabled only while the form is dirty.
const ScoringDefaults = ({
  data,
  loading,
  saving,
  alertLevels,
  fetchDefaultScoringConfigAction,
  saveDefaultScoringConfigAction,
  fetchAMLSAlertLevelAction
}) => {
  const [form, setForm] = useState(DEFAULT_DEVIATION_MAX_CONFIG);

  useEffect(() => {
    fetchDefaultScoringConfigAction();
  }, [fetchDefaultScoringConfigAction]);

  useEffect(() => {
    if (!alertLevels?.length) fetchAMLSAlertLevelAction();
  }, []);

  // Server-side baseline the form is compared against: the saved global default
  // when present, otherwise the shipped defaults.
  const baseline = useMemo(
    () =>
      data && data.engineType === 'DeviationMax'
        ? { ...DEFAULT_DEVIATION_MAX_CONFIG, ...data }
        : DEFAULT_DEVIATION_MAX_CONFIG,
    [data]
  );

  useEffect(() => {
    setForm(baseline);
  }, [baseline]);

  const showingDefaults = !data;
  const isDirty = useMemo(
    () => !scoringConfigEquals(form, baseline),
    [form, baseline]
  );

  // FIN-13794 — strict `0 <= lowMin < lowMax < mediumMax < 100` gates Save. FIN-14015 — band
  // messages are shown once, inline by the slider (same input: lowMin ?? 0); this list holds
  // only the other field errors.
  const validation = useMemo(() => deviationErrors(form), [form]);
  const bandsInvalid = useMemo(
    () =>
      bandErrors({ ...form.thresholds, lowMin: form.thresholds.lowMin ?? 0 })
        .length > 0,
    [form.thresholds]
  );

  const onSave = () => saveDefaultScoringConfigAction({ config: form });
  // Reset reverts to the shipped defaults in-UI (no server delete). Save then
  // persists them as the tenant default.
  const onReset = () => setForm(DEFAULT_DEVIATION_MAX_CONFIG);

  // FIN-13794 — `thresholds` grew an optional `lowMin` field. When absent (legacy tenants
  // pre-migration), the runtime treats it as 0 (no suppression); FE preserves that shape
  // by not writing the field until the admin sets a non-zero value.
  const updateThresholds = ({ lowMax, mediumMax, lowMin }) =>
    setForm((p) => ({
      ...p,
      thresholds: {
        ...p.thresholds,
        lowMax,
        mediumMax,
        ...(lowMin != null ? { lowMin } : {})
      }
    }));

  if (loading && !data) return <Spin />;

  return (
    <Content>
      <Space direction="vertical" className={style.tabPanelSpace}>
        <Content className={style.site_layout_background}>
          <div className={style['alert-scoring-tab']}>
            <div className={style.header}>
              <div className={style['header-title']}>Default Scoring</div>
              <div className={style['header-subtitle']}>
                Tenant-level default score thresholds and deviation behavior.
                Scenarios without a scenario-specific override inherit these
                defaults.
              </div>
            </div>

            {showingDefaults && (
              <Alert
                className={style['defaults-banner']}
                type="info"
                showIcon
                message="These are the built-in default settings. Click Save to make them the tenant defaults."
              />
            )}

            <div className={style.panel}>
              <PanelTitle tip={TIPS.bands}>Alert Level Bands</PanelTitle>
              {/* FIN-13794 (Q10 revised) — three MIN-oriented inputs (Low/Good/High); the
                  suppression floor (`lowMin`) is the "Low ≥" value. AlertLevelBandSlider
                  translates on the wire so the storage shape stays `{lowMin, lowMax, mediumMax}`
                  (FQ3 backward-compat). */}
              <AlertLevelBandSlider
                lowMin={form.thresholds.lowMin ?? 0}
                lowMax={form.thresholds.lowMax}
                mediumMax={form.thresholds.mediumMax}
                onChange={updateThresholds}
                disabled={false}
                alertLevels={alertLevels}
              />
            </div>

            <div className={style.panel}>
              <PanelTitle tip={TIPS.aggregation}>Aggregation Mode</PanelTitle>
              <Radio.Group
                value={form.aggregationMode}
                onChange={(e) =>
                  setForm((p) => ({ ...p, aggregationMode: e.target.value }))
                }
              >
                <Radio value="Max">{AGGREGATION_LABELS.Max}</Radio>
                <Radio value="SumCapped">{AGGREGATION_LABELS.SumCapped}</Radio>
              </Radio.Group>
            </div>

            <div className={style.panel}>
              <PanelTitle tip={TIPS.deviation}>Deviation Modifier</PanelTitle>
              <div className={style['switch-row']}>
                <Switch
                  id="deviationEnabled"
                  checked={form.deviationEnabled}
                  onChange={(v) =>
                    setForm((p) => ({ ...p, deviationEnabled: v }))
                  }
                />
                <label
                  htmlFor="deviationEnabled"
                  className={style['switch-label']}
                >
                  Enable deviation modifier
                </label>
              </div>
              {form.deviationEnabled && (
                <div className={style['deviation-fields']}>
                  <div className={style.field}>
                    <FieldLabel htmlFor="minFraction" tip={TIPS.minFraction}>
                      {FIELD_LABELS.minFraction}
                    </FieldLabel>
                    <InputNumber
                      id="minFraction"
                      aria-label={FIELD_LABELS.minFraction}
                      min={0}
                      max={1}
                      step={0.05}
                      value={form.minFraction}
                      onChange={(v) =>
                        setForm((p) => ({ ...p, minFraction: v }))
                      }
                    />
                  </div>
                  <div className={style.field}>
                    <FieldLabel htmlFor="capRatio" tip={TIPS.capRatio}>
                      {FIELD_LABELS.capRatio}
                    </FieldLabel>
                    <InputNumber
                      id="capRatio"
                      aria-label={FIELD_LABELS.capRatio}
                      min={1.01}
                      step={0.5}
                      value={form.capRatio}
                      onChange={(v) => setForm((p) => ({ ...p, capRatio: v }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {validation.length > 0 && (
              <Alert
                type="error"
                showIcon
                message={validation.join('; ')}
                style={{ marginBottom: 16 }}
              />
            )}

            <div className={style.footer}>
              <div className={style['footer-meta']}>
                These tenant defaults are applied at scoring time to any
                scenario that has no scenario-specific override.
              </div>
              <div className={style['footer-actions']}>
                <Button onClick={onReset} disabled={saving || !isDirty}>
                  Reset to defaults
                </Button>
                <Button
                  type="primary"
                  onClick={onSave}
                  loading={saving}
                  disabled={bandsInvalid || validation.length > 0}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </Content>
      </Space>
    </Content>
  );
};

ScoringDefaults.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  alertLevels: PropTypes.array,
  fetchDefaultScoringConfigAction: PropTypes.func.isRequired,
  saveDefaultScoringConfigAction: PropTypes.func.isRequired,
  fetchAMLSAlertLevelAction: PropTypes.func.isRequired
};
ScoringDefaults.defaultProps = {
  data: null,
  loading: false,
  saving: false,
  alertLevels: []
};

const mapStateToProps = (state) => ({
  data: state.defaultScoringConfig?.data,
  loading: state.defaultScoringConfig?.loading,
  saving: state.defaultScoringConfig?.saving,
  alertLevels: state.amlsCommon?.alertLevelConfigs?.data
});

const mapDispatchToProps = {
  fetchDefaultScoringConfigAction: fetchDefaultScoringConfig,
  saveDefaultScoringConfigAction: saveDefaultScoringConfig,
  fetchAMLSAlertLevelAction: fetchAMLSAlertLevel
};

export default connect(mapStateToProps, mapDispatchToProps)(ScoringDefaults);
